import { NativeSelect, NativeSelectOption } from '@worldcoo/ui/components/native-select';
import { Flag } from '@/lib/country-flags';
import { useLanguageHrefs, useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { Suspense, type MouseEvent } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { isNotFound } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';

const languageOptions = [
	{ value: 'ca' as const, label: 'Català' },
	{ value: 'de' as const, label: 'Deutsch' },
	{ value: 'en' as const, label: 'English' },
	{ value: 'es' as const, label: 'Español' },
	{ value: 'eu' as const, label: 'Euskera' },
	{ value: 'fr' as const, label: 'Français' },
	{ value: 'gl' as const, label: 'Galego' },
	{ value: 'it' as const, label: 'Italiano' },
	{ value: 'nl' as const, label: 'Nederlands' },
	{ value: 'pt' as const, label: 'Português' },
];

/** Desktop: compact native select with flag icon. */
export function LanguageSelect(props: { className?: string }) {
	return (
		<Suspense fallback={<LanguageSelectPrimitive languageOptions={languageOptions} />}>
			<SuspensedLanguageSelect className={props.className} />
		</Suspense>
	);
}

/** Mobile drawer: tappable list of languages with flags. */
export function MobileLanguageList(props: { onSelect?: () => void }) {
	return (
		<Suspense fallback={<MobileLanguageListPrimitive languageOptions={languageOptions} />}>
			<SuspensedMobileLanguageList onSelect={props.onSelect} />
		</Suspense>
	);
}

function SuspensedLanguageSelect(props: { className?: string }) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<LanguageSelectPrimitive
			languageOptions={languageOptions.filter((option) => metadata.locales.includes(option.value))}
			className={props.className}
		/>
	);
}

function SuspensedMobileLanguageList(props: { onSelect?: () => void }) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<MobileLanguageListPrimitive
			languageOptions={languageOptions.filter((option) => metadata.locales.includes(option.value))}
			onSelect={props.onSelect}
		/>
	);
}

function LanguageSelectPrimitive(props: { languageOptions: typeof languageOptions; className?: string }) {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	if (props.languageOptions.length <= 1) return;

	return (
		<div className={cn('flex items-center gap-1.5', props.className)}>
			<Flag locale={language} className="pointer-events-none size-5" />
			<NativeSelect
				className="[&_select]:border-none [&_select]:bg-transparent [&_select]:text-base [&_select]:shadow-none"
				name="language-select"
				value={language}
				onChange={(e) => {
					const selected = props.languageOptions.find((o) => o.value === e.target.value);
					if (selected) changeLanguage(selected.value);
				}}
				aria-label={t`Select language`}
			>
				{props.languageOptions.map(({ value, label }) => (
					<NativeSelectOption key={value} value={value}>
						{label}
					</NativeSelectOption>
				))}
			</NativeSelect>
		</div>
	);
}

function MobileLanguageListPrimitive(props: { languageOptions: typeof languageOptions; onSelect?: () => void }) {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const hrefs = useLanguageHrefs(props.languageOptions.map((o) => o.value));
	if (props.languageOptions.length <= 1) return;

	function handleClick(e: MouseEvent<HTMLAnchorElement>, lang: (typeof languageOptions)[number]['value']) {
		e.preventDefault();
		if (lang !== language) changeLanguage(lang);
		props.onSelect?.();
	}

	return (
		<ul role="listbox" aria-label={t`Select language`} className="flex flex-col gap-1 px-3">
			{props.languageOptions.map((option) => {
				const isActive = language === option.value;
				return (
					<li key={option.value} role="option" aria-selected={isActive}>
						<a
							href={hrefs[option.value]}
							className={cn(
								'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-base transition-colors',
								isActive ? 'bg-primary/10 font-medium' : 'hover:bg-muted',
							)}
							onClick={(e) => handleClick(e, option.value)}
						>
							<Flag locale={option.value} className="size-6" />
							<span className="flex-1 text-left">{option.label}</span>
							{isActive && <CheckIcon className="text-primary size-5" />}
						</a>
					</li>
				);
			})}
		</ul>
	);
}
